<template>
    <v-dialog v-model="dialog" fullscreen transition="slide-y-reverse-transition" persistent
        :style="`${isAndroidNative ? ' height: 100vh;' : ''} `">
        <div class="dialog-content">
            <!-- Close Button -->
            <v-btn icon class="close-btn" @click="closeDialog">
                <v-icon>mdi-close</v-icon>
            </v-btn>

            <!-- Spinner -->
            <div v-if="loading" class="spinner-overlay">
                <v-progress-circular indeterminate color="primary" :size="70" :width="7" />
            </div>

            <!-- Babylon.js container -->
            <div ref="sceneContainer" class="hbc-desktop-container"></div>

            <!-- Room popup (future use) -->
            <div v-if="selectedRoom" class="room-popup" :style="{ top: popupY + 'px', left: popupX + 'px' }">
                {{ selectedRoom }}
            </div>
        </div>
    </v-dialog>
</template>
<script setup>
import { ref, onBeforeUnmount, watch, nextTick, defineExpose } from 'vue';
import { Capacitor } from '@capacitor/core';
const isAndroidNative = Capacitor.getPlatform() === 'android' && Capacitor.isNative
import {
    Engine,
    Scene,
    ArcRotateCamera,
    Vector3,
    HemisphericLight,
    SceneLoader,
    Color3,
    StandardMaterial,
    Animation,
} from '@babylonjs/core';
import '@babylonjs/loaders/glTF';

const dialog = ref(false);
const loading = ref(false);
const sceneContainer = ref(null);

let engine = null;
let scene = null;
let canvas = null;

function openDialog() {
    dialog.value = true;
    loading.value = true;
}

function closeDialog() {
    dialog.value = false;
    loading.value = false;
    disposeScene();
}

function onResize() {
    engine?.resize();
}

// 🟢 All markers and text names from Blender
const markerNames = [
    "2.02_Auditorium_Marker",
    "2.06_FH Fixed_Desks_Marker",
    "2.10_Meeting_Room_Marker",
    "2.42_FH_Team_Marker",
    "Community_Area_Marker",
    "Pinestack_Team_Marker",
    "2.05_acitoflux_Marker"
];

const textNames = [
    "2.02_Auditorium_Text",
    "2.06_FH_Fixed_Desks_Text",
    "2.10_Meeting_Room_Text",
    "2.42_FH_Team_Text",
    "Community_Area_Text",
    "Pinestack_Team_Text",
    "2.05_acitoflux_Text"
];

function initBabylon() {
    if (!sceneContainer.value) return;

    canvas = document.createElement('canvas');
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    canvas.setAttribute('touch-action', 'none');
    sceneContainer.value.innerHTML = '';
    sceneContainer.value.appendChild(canvas);

    engine = new Engine(canvas, true, { preserveDrawingBuffer: true, stencil: true });
    scene = new Scene(engine);
    scene.clearColor = new Color3(0.02, 0.02, 0.05);

    const camera = new ArcRotateCamera('camera', Math.PI / 2, Math.PI / 3, 30, Vector3.Zero(), scene);
    camera.attachControl(canvas, true);
    camera.lowerRadiusLimit = 5;
    camera.upperRadiusLimit = 100;

    new HemisphericLight('light', new Vector3(0, 1, 0), scene).intensity = 1.2;

    scene.createDefaultEnvironment({
        createSkybox: true,
        skyboxSize: 1000,
        enableGroundShadow: true,
        groundYBias: 1,
    });

    const cfg = scene.imageProcessingConfiguration;
    cfg.toneMappingEnabled = true;
    cfg.exposure = 0.4;
    cfg.contrast = 2.5;
    cfg.saturation = 4.2;

    SceneLoader.ImportMeshAsync(null, '/assets/', 'HBC_EDIT.glb', scene)
        .then(({ meshes }) => {

            meshes.forEach(element => {
                console.log(element.name, element);
            });
            markerNames.forEach(name => {

                const marker = meshes.find(m => m.name === name);

                if (marker) {
                    const mat = new StandardMaterial("markerMat_" + name, scene);
                    mat.emissiveColor = new Color3(0.3, 1, 0.5);
                    mat.diffuseColor = new Color3(0.1, 0.6, 0.2);
                    marker.material = mat;

                    const baseScale = marker.scaling.clone();
                    const pulse = new Animation("pulse_" + name, "scaling", 30, Animation.ANIMATIONTYPE_VECTOR3);
                    pulse.setKeys([
                        { frame: 0, value: baseScale },
                        { frame: 15, value: baseScale.multiplyByFloats(1.15, 1.15, 1.15) },
                        { frame: 30, value: baseScale }
                    ]);
                    marker.animations = [pulse];
                    scene.beginAnimation(marker, 0, 30, true);
                }
            });

            // ✨ Apply to all texts
            textNames.forEach(name => {
                const textMesh = meshes.find(m => m.name === name);
                if (textMesh) {
                    const textMat = new StandardMaterial("textMat_" + name, scene);
                    textMat.emissiveColor = new Color3(1, 0.9, 1); // glowing white
                    textMat.diffuseColor = new Color3(1, 1, 1);
                    textMat.specularColor = new Color3(1, 0.3, 1); // purple edge
                    textMat.alpha = 1;
                    textMat.backFaceCulling = false;
                    textMesh.material = textMat;

                    textMesh.position.y += 0.01;
                    textMesh.receiveShadows = false;
                }
            });

            loading.value = false;
        })
        .catch(err => {
            console.error('Error loading model:', err);
            loading.value = false;
            alert('Failed to load the 3D model.');
        });

    engine.runRenderLoop(() => {
        if (scene) scene.render();
    });

    window.addEventListener('resize', onResize);
}

function disposeScene() {
    if (scene) scene.dispose(), scene = null;
    if (engine) engine.dispose(), engine = null;
    if (canvas?.parentNode) canvas.parentNode.removeChild(canvas);
    window.removeEventListener('resize', onResize);
}

watch(dialog, val => {
    val ? nextTick(initBabylon) : disposeScene();
});

onBeforeUnmount(disposeScene);

defineExpose({ openDialog, closeDialog });
</script>


<style scoped>
.dialog-content {
    background: #1e1e1e;
    width: 100%;
    height: 100%;
    position: relative;
    overflow: hidden;
}

.hbc-desktop-container {
    width: 100%;
    height: 100vh;
    overflow: hidden;
}

.spinner-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    background: rgba(0, 0, 0, 0.5);
    z-index: 20;
}

.room-popup {
    position: absolute;
    background: rgba(0, 0, 0, 0.8);
    color: white;
    padding: 8px 12px;
    border-radius: 5px;
    font-size: 14px;
    pointer-events: none;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.3);
    z-index: 10;
}

.close-btn {
    position: absolute;
    top: 16px;
    right: 16px;
    z-index: 30;
    background: rgba(0, 0, 0, 0.7);
    color: white;
}
</style>